<template>
    <ChartDropdownMenu @menu-size="onMenuSize" :menu="menu" :timeframes="timeframes" :chartTypes="chartTypes"
        :chartType="chartType" :renkoBrick="renkoBrick" :tab="tab" :q="q" :filteredStudies="filteredStudies"
        :currentStudies="currentStudies" @select-timeframe="$emit('select-timeframe', $event)"
        @select-chart-type="$emit('select-chart-type', $event)" @apply-chart-type="$emit('apply-chart-type')"
        @update:renkoBrick="$emit('update:renkoBrick', $event)" @add-study="addStudy" @remove-study="removeStudy"
        @clear-all-studies="clearAllStudies" @update:tab="tab = $event" @update:q="q = $event" />
    <div v-if="edit.open" ref="editEl"
        class="position-fixed popover bg-white border rounded shadow-sm small overflow-hidden" @click.stop>
        <div v-if="String(edit.study?.name || '').toUpperCase() === 'MA'" class="px-3 py-2 enabled-action"
            @click="openSettings(edit.study)">Edit Settings…
        </div>
        <div class="px-3 py-1 danger" @click="removeStudy(edit.study)">Delete Study</div>
    </div>
    <StudySettingsModal v-if="settings.open" :key="settings.id" v-model="settings.open" :title="settings.title"
        :kind="settings.name" :settings="settings" @apply="onModalApply" @close="closeSettings" />
</template>
<script>
import ChartDropdownMenu from "./ChartDropdownMenu.vue";
import StudySettingsModal from "./StudySettingsModal.vue";
const overlay_set = new Set(["MA", "EMA", "SMA", "BOLL", "SAR", "BBI", "AVP"]);
export default {
    name: "CandlesChartStudies",
    components: { ChartDropdownMenu, StudySettingsModal },
    emits: ["select-timeframe", "select-chart-type", "apply-chart-type", "update:renkoBrick", "studies-changed"],
    props: {
        chart: { type: Object, default: null },
        mainPaneId: { type: String, default: "candle_pane" },
        timeframes: { type: Array, required: true },
        chartTypes: { type: Array, required: true },
        tf: { type: Object, default: null },
        chartType: { type: Object, default: null },
        renkoBrick: { type: Number, default: 200 },
    },
    data() {
        return {
            menu: { open: false, kind: null, left: 0, top: 0, width: 0, height: 0, maxHeight: 0, anchorEl: null },
            tab: "studies",
            q: "",
            edit: { open: false, x: 0, y: 0, study: null },
            settings: {
                open: false,
                id: null,
                name: "",
                title: "",
                paneId: "",
                panel: "auto",
                calcParams: [],
                extra: {},
                style: {},
            },
            builtIn: [
                { key: "MA", label: "Moving Average" },
                { key: "EMA", label: "Exponential Moving Average" },
                { key: "SMA", label: "Simple Moving Average" },
                { key: "BBI", label: "Bull and Bear Index" },
                { key: "VOL", label: "Volume" },
                { key: "MACD", label: "Moving Average Convergence Divergence" },
                { key: "BOLL", label: "Bollinger Bands" },
                { key: "KDJ", label: "KDJ Oscillator (Stochastic Variant)" },
                { key: "RSI", label: "Relative Strength Index" },
                { key: "BIAS", label: "Bias Ratio" },
                { key: "BRAR", label: "BRAR Momentum Indicator" },
                { key: "CCI", label: "Commodity Channel Index" },
                { key: "DMI", label: "Directional Movement Index" },
                { key: "CR", label: "CR Momentum Indicator" },
                { key: "PSY", label: "Psychological Line" },
                { key: "DMA", label: "Difference of Moving Averages" },
                { key: "TRIX", label: "Triple Exponential Average" },
                { key: "OBV", label: "On-Balance Volume" },
                { key: "VR", label: "Volume Ratio" },
                { key: "WR", label: "Williams %R" },
                { key: "MTM", label: "Momentum Indicator" },
                { key: "EMV", label: "Ease of Movement" },
                { key: "SAR", label: "Parabolic Stop and Reverse" },
                { key: "AO", label: "Awesome Oscillator" },
                { key: "ROC", label: "Rate of Change" },
                { key: "PVT", label: "Price Volume Trend" },
                { key: "AVP", label: "Average Volume Price" },
            ],
            currentStudies: [],
        };
    },
    computed: {
        getStudies() {
            return this.builtIn.map((x) => ({ key: `builtin:${x.key}`, label: `${x.key} – ${x.label}` }));
        },
        filteredStudies() {
            const term = (this.q || "").trim().toLowerCase();
            if (!term) return this.getStudies;
            return this.getStudies.filter((s) => s.label.toLowerCase().includes(term));
        },
    },
    mounted() {
        document.addEventListener("click", this.onGlobalClick);
        window.addEventListener("scroll", this.onWindowMove, true);
        window.addEventListener("resize", this.onWindowMove);
    },
    beforeUnmount() {
        document.removeEventListener("click", this.onGlobalClick);
        window.removeEventListener("scroll", this.onWindowMove, true);
        window.removeEventListener("resize", this.onWindowMove);
    },
    methods: {
        toStudyPayload(study) {
            if (!study?.id) return null;
            const ind = this.chart?.getIndicators?.({ id: study.id })?.[0];
            const name = String(ind?.name || study.name || "").toUpperCase();
            const calcParams = Array.isArray(ind?.calcParams) ? [...ind.calcParams] : [];
            const ex = ind?.extendData || {};
            let key = name.toLowerCase();
            let label = name;
            if (name === "MA") {
                const period = Number(calcParams?.[0] ?? 50);
                key = `ma${period}`;
                label = `MA(${period})`;
            } else if (name === "RSI") {
                const period = Number(calcParams?.[0] ?? 14);
                key = `rsi${period}`;
                label = `RSI(${period})`;
            } else if (name === "EMA") {
                const period = Number(calcParams?.[0] ?? 50);
                key = `ema${period}`;
                label = `EMA(${period})`;
            } else if (name === "SMA") {
                const period = Number(calcParams?.[0] ?? 50);
                key = `sma${period}`;
                label = `SMA(${period})`;
            }
            return {
                id: study.id,
                name,
                key,
                label,
                paneId: ind?.paneId ?? study.paneId ?? "",
                calcParams,
                extra: { ...ex }
            };
        },
        emitStudiesChanged() {
            const list = (this.currentStudies || [])
                .map(s => this.toStudyPayload(s))
                .filter(Boolean);
            this.$emit("studies-changed", list);
        },
        openMenu(kind, anchorEl) {
            this.menu.kind = kind;
            this.menu.open = true;
            this.menu.anchorEl = anchorEl;
        },
        onMenuSize({ width, height }) {
            this.menu.width = Math.ceil(width);
            this.menu.height = Math.ceil(height);
            if (this.menu.open && this.menu.anchorEl) {
                this.positionMenu(this.menu.width, this.menu.height);
            }
        },
        onWindowMove() {
            if (!this.menu.open) return;
            if (!this.menu.width) return;
            this.positionMenu(this.menu.width, this.menu.height);
        },
        positionMenu(width, height) {
            if (!this.menu.anchorEl) return;
            const r = this.menu.anchorEl.getBoundingClientRect();
            const margin = 8;
            let left = r.right - width;
            left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
            const spaceBelow = window.innerHeight - r.bottom - margin;
            const spaceAbove = r.top - margin;
            const openBelow = spaceBelow >= spaceAbove;
            let top = openBelow ? (r.bottom + 6) : (r.top - height - 6);
            top = Math.max(margin, top);
            const maxHeight = openBelow ? (spaceBelow - 6) : (spaceAbove - 6);
            this.menu.left = left;
            this.menu.top = top;
            this.menu.maxHeight = Math.max(0, Math.floor(maxHeight));
        },
        closeMenu() {
            this.menu.open = false;
            this.menu.kind = null;
            this.menu.anchorEl = null;
        },
        onGlobalClick(e) {
            if (this.menu.open && this.menu.anchorEl && this.menu.anchorEl.contains(e.target)) return;
            this.closeMenu();
            this.edit.open = false;
        },
        clamp(n, min, max) {
            return Math.max(min, Math.min(max, n));
        },
        openContextMenu({ clientX, clientY }) {
            if (!this.currentStudies.length) return;
            const study = this.currentStudies[this.currentStudies.length - 1];
            if (!study) return;
            const margin = 8;
            this.edit.open = true;
            this.edit.study = study;
            this.$nextTick(() => {
                const el = this.$refs.editEl;
                if (!el) return;
                const { width, height } = el.getBoundingClientRect();
                const x = Math.max(
                    margin,
                    Math.min(clientX, window.innerWidth - width - margin)
                );
                const y = Math.max(
                    margin,
                    Math.min(clientY, window.innerHeight - height - margin)
                );
                el.style.left = `${x}px`;
                el.style.top = `${y}px`;
            });
        },
        isOverlay(name) {
            return overlay_set.has(String(name).toUpperCase());
        },
        addStudy(item) {
            if (!this.chart) return;
            if (this.currentStudies.some((x) => x.key === item.key)) return;
            const [, shortKeyRaw] = item.key.split(":");
            const name = String(shortKeyRaw || "").toUpperCase();
            const overlay = this.isOverlay(name);
            const paneHeight = this.getIndicatorPaneHeight();
            const id = overlay
                ? this.chart.createIndicator({ name, shouldOhlc: true }, true, { id: this.mainPaneId })
                : this.chart.createIndicator({ name, shouldOhlc: false }, false, { height: paneHeight, dragEnabled: true });

            if (!id) return;
            const ind = this.chart.getIndicators?.({ id })?.[0];
            this.currentStudies.push({
                key: item.key,
                label: item.label,
                id,
                name,
                paneId: ind?.paneId ?? (overlay ? this.mainPaneId : ""),
            });
            this.emitStudiesChanged();
        },
        getIndicatorPaneHeight() {
            const engineEl = this.$refs.engine?.getRootEl?.();
            const rect = engineEl?.getBoundingClientRect?.();

            const chartH = rect?.height || window.innerHeight;
            return Math.floor(chartH * 0.25);
        },
        removeStudy(study) {
            try {
                if (study?.id) this.chart?.removeIndicator?.({ id: study.id });
            } finally {
                this.currentStudies = this.currentStudies.filter((x) => x.id !== study?.id);
                if (this.settings.id === study?.id) this.closeSettings();
                if (this.edit.study?.id === study?.id) this.edit.open = false;
                this.emitStudiesChanged();
            }
        },
        clearAllStudies() {
            this.chart?.removeIndicator?.();
            this.currentStudies = [];
            this.closeSettings();
            this.edit.open = false;
            this.emitStudiesChanged();
        },
        openSettings(study) {
            this.edit.open = false;
            if (!study || !study.id) return;
            const ind = this.chart?.getIndicators?.({ id: study.id })?.[0];
            const name = String(ind?.name || study.name || "").toUpperCase();
            if (name !== "MA") return;
            const currentColor =
                ind?.styles?.lines?.[0]?.color ||
                ind?.styles?.line?.color ||
                ind?.styles?.color ||
                null;
            const ex = ind?.extendData || {};
            const currentField = ex.field || "close";
            const currentType = ex.type || "Simple";
            const currentOffset = Number(ex.offset ?? 0);
            const currentUnderlay = !!ex.underlay;
            this.settings.open = true;
            this.settings.id = study.id;
            this.settings.name = name;
            this.settings.title = "Moving Average";
            this.settings.paneId = ind?.paneId || study.paneId || "";
            this.settings.panel = this.settings.paneId === this.mainPaneId ? "overlay" : "pane";
            this.settings.calcParams = Array.isArray(ind?.calcParams) ? [...ind.calcParams] : [];
            this.settings.extra = {
                field: currentField,
                type: currentType,
                offset: currentOffset,
                underlay: currentUnderlay,
            };
            this.settings.style = currentColor ? { line: { color: currentColor } } : {};
        },
        closeSettings() {
            this.settings.open = false;
            this.settings.id = null;
            this.settings.name = "";
            this.settings.title = "";
            this.settings.paneId = "";
            this.settings.panel = "auto";
            this.settings.calcParams = [];
            this.settings.extra = {};
            this.settings.style = {};
        },
        onModalApply(payload) {
            this.settings.panel = payload.panel;
            this.settings.calcParams = [...payload.calcParams];
            this.settings.extra = { ...(this.settings.extra || {}), ...(payload.extra || {}) };
            this.settings.style = payload.style || {};
            this.applySettings();
        },
        applySettings() {
            const id = this.settings.id;
            if (!id) return;
            const ind = this.chart?.getIndicators?.({ id })?.[0];
            if (!ind) return this.closeSettings();
            const name = String(ind.name || "").toUpperCase();
            const desired = this.settings.panel === "auto" ? (this.isOverlay(name) ? "overlay" : "pane") : this.settings.panel;
            const nowOverlay = ind.paneId === this.mainPaneId;
            const wantOverlay = desired === "overlay";
            const color = this.settings?.style?.line?.color || null;
            const styles = color ? { lines: [{ color }] } : undefined;
            const extendData = {
                ...(ind.extendData || {}),
                ...(this.settings.extra || {}),
            };
            const underlay = !!extendData.underlay;
            const zLevel = underlay ? -10 : 10;
            const zIndex = underlay ? -10 : 10;
            if (nowOverlay === wantOverlay) {
                this.chart?.overrideIndicator?.({
                    id, name, calcParams: [...this.settings.calcParams], extendData, ...(styles ? { styles } : {}), zLevel, zIndex,
                });
                this.closeSettings();
                this.emitStudiesChanged();
                return;
            }
            this.chart?.removeIndicator?.({ id });
            const paneHeight = this.getIndicatorPaneHeight();
            const newId = wantOverlay
                ? this.chart.createIndicator(
                    { name, calcParams: [...this.settings.calcParams], shouldOhlc: true, extendData, ...(styles ? { styles } : {}), zLevel, zIndex },
                    true,
                    { id: this.mainPaneId }
                )
                : this.chart.createIndicator(
                    { name, calcParams: [...this.settings.calcParams], shouldOhlc: false, extendData, ...(styles ? { styles } : {}), zLevel, zIndex },
                    false,
                    { height: paneHeight, dragEnabled: true }
                );
            const created = this.chart?.getIndicators?.({ id: newId })?.[0];
            this.currentStudies = this.currentStudies.map((s) =>
                s.id === id ? { ...s, id: newId, name, paneId: created?.paneId ?? (wantOverlay ? this.mainPaneId : "") } : s
            );
            this.closeSettings();
            this.emitStudiesChanged();
        },
    },
};
</script>
